import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { authFetch, apiErr } from "../../lib/adminApi";
import { AdminBar } from "./AdminCore";
import { FieldEditor } from "./EditorFields";

const API = process.env.REACT_APP_BACKEND_URL;

const AREA_FIELDS = [
  { section: "Search engines", fields: [
    { k: "seo_title", t: "text", label: "SEO title" },
    { k: "seo_description", t: "textarea", label: "Meta description" },
  ]},
  { section: "Hero", fields: [
    { k: "h1", t: "text", label: "H1 heading" },
    { k: "hero_sub", t: "textarea", label: "Hero subtitle" },
    { k: "hero_image", t: "image", label: "Hero image (blank = default)" },
    { k: "hero_alt", t: "text", label: "Hero image alt text" },
    { k: "cta", t: "object", label: "Calls to action", fields: [
      { k: "label", t: "text", label: "Hero button text" },
      { k: "heading", t: "text", label: "Showroom CTA heading" }] },
  ]},
  { section: "Copy", fields: [
    { k: "intro_title", t: "text", label: "Intro section heading" },
    { k: "intro", t: "lines", label: "Intro paragraphs" },
    { k: "local_title", t: "text", label: "Local knowledge heading" },
    { k: "local", t: "lines", label: "Local knowledge paragraphs" },
  ]},
  { section: "Local project evidence", fields: [
    { k: "projects", t: "items", label: "Project tiles (blank = defaults)", fields: [
      { k: "img", t: "image", label: "Photo" }, { k: "title", t: "text", label: "Title" },
      { k: "loc", t: "text", label: "Location" }, { k: "to", t: "text", label: "Link (e.g. /projects/ or a full project page)" }] },
  ]},
  { section: "Product tiles", fields: [
    { k: "products", t: "items", label: "Product tiles (blank = defaults)", fields: [
      { k: "img", t: "image", label: "Image" }, { k: "label", t: "text", label: "Label" },
      { k: "to", t: "text", label: "Link" }, { k: "alt", t: "text", label: "Image alt text" }] },
  ]},
  { section: "FAQs", fields: [
    { k: "faqs", t: "items", label: "Frequently asked questions", fields: [
      { k: "q", t: "text", label: "Question" }, { k: "a", t: "textarea", label: "Answer" }] },
  ]},
  { section: "Internal links", fields: [
    { k: "nearby", t: "lines", label: "Nearby area slugs (one per row, e.g. guildford)" },
  ]},
];

const KEYS = AREA_FIELDS.flatMap((s) => s.fields.map((f) => f.k));

export default function AdminAreaEditor() {
  const [params] = useSearchParams();
  const slug = params.get("slug");
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Admin · Edit Area | Cedar Views";
    fetch(`${API}/api/areas/${slug}`).then((r) => r.json()).then(setArea)
      .catch(() => toast.error("Could not load this area — check your connection and refresh"));
  }, [slug]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      KEYS.forEach((k) => { if (area[k] !== undefined) payload[k] = area[k]; });
      const res = await authFetch(`/api/areas/${slug}`, { method: "PUT", body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { toast.error(apiErr(data.detail)); return; }
      toast.success(`${area.town} saved — changes are live`);
    } finally { setBusy(false); }
  };

  if (!area) return <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="area-editor-loading" />;

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-area-editor">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title={`Edit ${area.town}.`} />
        <div className="flex items-center justify-between gap-4 mb-10 -mt-4">
          <Link to="/admin/areas" className="text-xs font-semibold tracking-[0.15em] uppercase text-white/60 hover:text-white transition-colors">← All areas</Link>
          <div className="flex items-center gap-3">
            <a href={`/areas/${slug}/`} target="_blank" rel="noopener noreferrer" className="btn-ghost"><ExternalLink size={13} /> View page</a>
            <button onClick={save} disabled={busy} data-testid="area-save-btn" className="btn-solid disabled:opacity-40">
              <Save size={14} /> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        <div className="space-y-10">
          {AREA_FIELDS.map((sec) => (
            <div key={sec.section} className="border border-white/12 p-8" data-testid={`area-section-${sec.section.toLowerCase().replaceAll(" ", "-")}`}>
              <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-[#5B7F9E] mb-6">{sec.section}</p>
              <div className="space-y-6">
                {sec.fields.map((f) => (
                  <FieldEditor key={f.k} def={f} prefix={`area-${f.k}`} value={area[f.k]}
                    onChange={(v) => setArea({ ...area, [f.k]: v })} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <button onClick={save} disabled={busy} data-testid="area-save-btn-bottom" className="btn-solid disabled:opacity-40 mt-10">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </main>
  );
}
